import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { FolderOpen, Edit2, Trash2, Plus, GripVertical, X } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
// Auth-Header für /api/categories (Routen sind jetzt tenant-gescoped)
const getAuthHeaders = () => {
  const t = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};


// Sortable Category Item Component
function SortableCategory({ category, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="mb-3">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
              >
                <GripVertical className="w-5 h-5" />
              </div>
              <span className="text-2xl">{category.icon}</span>
              <span className="font-medium text-lg">{category.name}</span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onEdit(category)}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDelete(category.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

const CategoryManager = ({ companyId, onCategoriesChange, open, onOpenChange }) => {
  const [categories, setCategories] = useState([])
  const [editingCategory, setEditingCategory] = useState(null)
  const [newCategory, setNewCategory] = useState({ name: '', icon: '📦' })
  const [loading, setLoading] = useState(false)

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || ''

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (open && companyId) {
      fetchCategories()
    }
  }, [open, companyId])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      // First: auto-sync categories from services (idempotent, creates missing ones)
      await fetch(`${BACKEND_URL}/api/categories/sync-from-services/${companyId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })
      // Then load the now-complete category list
      const categoriesRes = await fetch(`${BACKEND_URL}/api/categories/${companyId}`, { headers: getAuthHeaders() })
      if (categoriesRes.ok) {
        const cats = await categoriesRes.json()
        setCategories(cats)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('❌ Fehler beim Laden der Daten')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCategory = async () => {
    if (!newCategory.name) {
      toast.error('❌ Bitte Kategorie-Namen eingeben')
      return
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/categories/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          ...newCategory,
          companyId: companyId
        })
      })

      if (response.ok) {
        toast.success('✅ Kategorie erstellt')
        setNewCategory({ name: '', icon: '📦' })
        fetchCategories()
        if (onCategoriesChange) onCategoriesChange()
      }
    } catch (error) {
      toast.error('❌ Fehler beim Erstellen')
    }
  }

  const handleUpdateCategory = async (categoryId, updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updates)
      })

      if (response.ok) {
        toast.success('✅ Kategorie aktualisiert')
        setEditingCategory(null)
        fetchCategories()
        if (onCategoriesChange) onCategoriesChange()
      }
    } catch (error) {
      toast.error('❌ Fehler beim Aktualisieren')
    }
  }

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Kategorie wirklich löschen? Services behalten ihre Kategorie-Zuordnung.')) {
      return
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      })

      if (response.ok) {
        toast.success('✅ Kategorie gelöscht')
        fetchCategories()
        if (onCategoriesChange) onCategoriesChange()
      }
    } catch (error) {
      toast.error('❌ Fehler beim Löschen')
    }
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id)
      const newIndex = categories.findIndex((cat) => cat.id === over.id)

      const reorderedCategories = arrayMove(categories, oldIndex, newIndex)
      setCategories(reorderedCategories)

      // Update orders in backend - batch update without triggering parent refresh
      try {
        const updatePromises = reorderedCategories.map((cat, i) =>
          fetch(`${BACKEND_URL}/api/categories/${cat.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ order: i })
          })
        )
        
        await Promise.all(updatePromises)
        toast.success('✅ Reihenfolge aktualisiert')
        // Don't call onCategoriesChange during drag & drop to prevent dialog flickering
      } catch (error) {
        toast.error('❌ Fehler beim Sortieren')
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      // When dialog closes, trigger parent refresh
      if (!isOpen && onCategoriesChange) {
        onCategoriesChange()
      }
      onOpenChange(isOpen)
    }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Kategorien verwalten
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          {/* Create New Category */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-4">Neue Kategorie erstellen</h3>
              <div className="grid grid-cols-[1fr,auto,auto] gap-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. Marketing Services"
                    onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                  />
                </div>
                <div className="w-32">
                  <Label>Icon</Label>
                  <select
                    value={newCategory.icon}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, icon: e.target.value }))}
                    className="w-full px-3 py-2 text-2xl text-center border rounded-md"
                  >
                    <option value="🎨">🎨 Design</option>
                    <option value="📝">📝 Formulare</option>
                    <option value="🔍">🔍 SEO</option>
                    <option value="📢">📢 Ads</option>
                    <option value="📱">📱 Social</option>
                    <option value="⚙️">⚙️ Tech</option>
                    <option value="🔧">🔧 Support</option>
                    <option value="✍️">✍️ Content</option>
                    <option value="🌐">🌐 Hosting</option>
                    <option value="📦">📦 Allgemein</option>
                    <option value="💼">💼 Business</option>
                    <option value="🎯">🎯 Marketing</option>
                    <option value="📊">📊 Analytics</option>
                    <option value="🛒">🛒 E-Commerce</option>
                    <option value="📧">📧 Email</option>
                    <option value="🎥">🎥 Video</option>
                    <option value="📷">📷 Foto</option>
                    <option value="🎬">🎬 Produktion</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleCreateCategory} disabled={loading}>
                    <Plus className="w-4 h-4 mr-2" />
                    Erstellen
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Edit Category Dialog */}
          {editingCategory && (
            <Card className="mb-6 border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Kategorie bearbeiten</h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingCategory(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-[1fr,auto,auto] gap-3">
                  <Input
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Kategorie-Name"
                  />
                  <select
                    value={editingCategory.icon}
                    onChange={(e) => setEditingCategory(prev => ({ ...prev, icon: e.target.value }))}
                    className="w-32 px-3 py-2 text-2xl text-center border rounded-md"
                  >
                    <option value="🎨">🎨 Design</option>
                    <option value="📝">📝 Formulare</option>
                    <option value="🔍">🔍 SEO</option>
                    <option value="📢">📢 Ads</option>
                    <option value="📱">📱 Social</option>
                    <option value="⚙️">⚙️ Tech</option>
                    <option value="🔧">🔧 Support</option>
                    <option value="✍️">✍️ Content</option>
                    <option value="🌐">🌐 Hosting</option>
                    <option value="📦">📦 Allgemein</option>
                    <option value="💼">💼 Business</option>
                    <option value="🎯">🎯 Marketing</option>
                    <option value="📊">📊 Analytics</option>
                    <option value="🛒">🛒 E-Commerce</option>
                    <option value="📧">📧 Email</option>
                    <option value="🎥">🎥 Video</option>
                    <option value="📷">📷 Foto</option>
                    <option value="🎬">🎬 Produktion</option>
                  </select>
                  <Button
                    onClick={() => handleUpdateCategory(editingCategory.id, {
                      name: editingCategory.name,
                      icon: editingCategory.icon
                    })}
                  >
                    Speichern
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Categories List with Drag & Drop */}
          <div className="mb-6">
            <h3 className="font-semibold mb-3">
              Kategorien sortieren ({categories.length})
              <span className="text-sm font-normal text-slate-500 ml-2">
                Ziehen Sie die Kategorien, um sie zu sortieren
              </span>
            </h3>
            
            {loading ? (
              <div className="text-center py-8 text-slate-500">Laden...</div>
            ) : categories.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Noch keine Kategorien vorhanden. Erstellen Sie die erste Kategorie oben.
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={categories.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {categories.map((category) => (
                    <SortableCategory
                      key={category.id}
                      category={category}
                      onEdit={setEditingCategory}
                      onDelete={handleDeleteCategory}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default CategoryManager
